import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../../services/api';
import {
  CheckCircle2, XCircle, Clock, Edit3, AlertTriangle,
  RefreshCw, ChevronDown, Shield, Zap, Filter
} from 'lucide-react';

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
};
const STONE = {
  50: '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4',
  400: '#a8a29e', 500: '#78716c', 600: '#57534e',
  700: '#44403c', 900: '#1c1917',
};

const STATUS_COLORS = {
  pending: { bg: '#fef3c7', text: '#92400e', icon: Clock },
  accepted: { bg: '#dcfce7', text: '#166534', icon: CheckCircle2 },
  rejected: { bg: '#fef2f2', text: '#991b1b', icon: XCircle },
  deferred: { bg: '#f5f5f4', text: '#57534e', icon: Clock },
  customized: { bg: '#ede9fe', text: '#5b21b6', icon: Edit3 },
};

export default function EnforcementReviewPanel({ skillId, onBack }) {
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [customizeModal, setCustomizeModal] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [candRes, statsRes] = await Promise.all([
        api.getEnforcementCandidates(skillId),
        api.getCandidateStats(skillId),
      ]);
      if (candRes.ok) setCandidates(candRes.data || []);
      if (statsRes.ok) setStats(statsRes.data || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [skillId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAccept = async (id) => {
    setActionLoading(id);
    try {
      const res = await api.acceptCandidate(id);
      if (res.ok) await loadData();
    } catch (e) { console.error(e); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (id, reason) => {
    setActionLoading(id);
    try {
      const res = await api.rejectCandidate(id, reason);
      if (res.ok) { setRejectModal(null); await loadData(); }
    } catch (e) { console.error(e); }
    finally { setActionLoading(null); }
  };

  const handleDefer = async (id) => {
    setActionLoading(id);
    try {
      const res = await api.deferCandidate(id);
      if (res.ok) await loadData();
    } catch (e) { console.error(e); }
    finally { setActionLoading(null); }
  };

  const handleCustomize = async (id, payload) => {
    setActionLoading(id);
    try {
      const res = await api.customizeCandidate(id, payload);
      if (res.ok) { setCustomizeModal(null); await loadData(); }
    } catch (e) { console.error(e); }
    finally { setActionLoading(null); }
  };

  const handleBatchAccept = async () => {
    setActionLoading('batch');
    try {
      const res = await api.batchAcceptCandidates(skillId, 75);
      if (res.ok) await loadData();
    } catch (e) { console.error(e); }
    finally { setActionLoading(null); }
  };

  const filtered = statusFilter === 'all' ? candidates
    : candidates.filter(c => c.status === statusFilter);

  const pendingCount = candidates.filter(c => c.status === 'pending').length;
  const acceptedCount = candidates.filter(c => c.status === 'accepted' || c.status === 'customized').length;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: STONE[400] }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
        <div style={{ fontSize: 13 }}>Loading enforcement candidates...</div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: STONE[900], margin: 0 }}>
            Enforcement Review
          </h2>
          <p style={{ fontSize: 12, color: STONE[500], margin: '4px 0 0' }}>
            {candidates.length} candidates — {acceptedCount} accepted, {pendingCount} pending review
          </p>
        </div>
        {pendingCount > 0 && (
          <button onClick={handleBatchAccept} disabled={actionLoading === 'batch'} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
            fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none',
            background: BRAND.primary, color: '#fff',
            cursor: actionLoading === 'batch' ? 'not-allowed' : 'pointer',
          }}>
            <Zap size={14} /> Accept All High-Confidence
          </button>
        )}
      </div>

      {/* Stats Bar */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 16, padding: '10px 14px',
        background: STONE[50], borderRadius: 10,
      }}>
        {Object.entries(stats.byStatus || {}).map(([status, count]) => {
          const sc = STATUS_COLORS[status] || STATUS_COLORS.pending;
          return (
            <button key={status} onClick={() => setStatusFilter(status === statusFilter ? 'all' : status)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
              fontSize: 11, fontWeight: 600, borderRadius: 6,
              border: statusFilter === status ? `1px solid ${sc.text}40` : '1px solid transparent',
              background: statusFilter === status ? sc.bg : 'transparent',
              color: sc.text, cursor: 'pointer',
            }}>
              {status}: {count}
            </button>
          );
        })}
        {statusFilter !== 'all' && (
          <button onClick={() => setStatusFilter('all')} style={{
            padding: '4px 10px', fontSize: 11, border: 'none', background: 'none',
            color: STONE[500], cursor: 'pointer', marginLeft: 'auto',
          }}>
            Clear Filter
          </button>
        )}
      </div>

      {/* Candidate List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.map(c => (
          <CandidateCard
            key={c.id}
            candidate={c}
            loading={actionLoading === c.id}
            onAccept={() => handleAccept(c.id)}
            onReject={() => setRejectModal(c)}
            onDefer={() => handleDefer(c.id)}
            onCustomize={() => setCustomizeModal(c)}
          />
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: STONE[400], fontSize: 13 }}>
            No candidates match "{statusFilter}"
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <RejectModal
          candidate={rejectModal}
          onReject={(reason) => handleReject(rejectModal.id, reason)}
          onClose={() => setRejectModal(null)}
          loading={actionLoading === rejectModal.id}
        />
      )}

      {/* Customize Modal */}
      {customizeModal && (
        <CustomizeModal
          candidate={customizeModal}
          onSave={(payload) => handleCustomize(customizeModal.id, payload)}
          onClose={() => setCustomizeModal(null)}
          loading={actionLoading === customizeModal.id}
        />
      )}
    </div>
  );
}

function CandidateCard({ candidate: c, loading, onAccept, onReject, onDefer, onCustomize }) {
  const [expanded, setExpanded] = useState(false);
  const sc = STATUS_COLORS[c.status] || STATUS_COLORS.pending;
  const StatusIcon = sc.icon;
  const confColor = c.confidenceScore >= 75 ? '#16a34a' : c.confidenceScore >= 50 ? '#ca8a04' : BRAND.primary;
  const isPending = c.status === 'pending' || c.status === 'deferred';

  return (
    <div style={{
      border: `1px solid ${STONE[200]}`, borderRadius: 12, background: '#fff', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: sc.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <StatusIcon size={16} color={sc.text} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: STONE[900] }}>
            {c.proposedRuleName}
          </div>
          <div style={{ fontSize: 11, color: STONE[500], display: 'flex', gap: 10, marginTop: 2, flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace", padding: '1px 6px', borderRadius: 4,
              background: STONE[100],
            }}>{c.proposedRuleType}</span>
            <span>{c.proposedSeverity}</span>
            <span style={{
              padding: '1px 6px', borderRadius: 4, background: sc.bg, color: sc.text,
            }}>{c.status}</span>
          </div>
        </div>

        <div style={{
          fontSize: 16, fontWeight: 700, color: confColor, textAlign: 'right',
          fontFamily: "'IBM Plex Mono', monospace", minWidth: 50,
        }}>
          {c.confidenceScore}%
        </div>

        {/* Actions */}
        {isPending && (
          <div style={{ display: 'flex', gap: 4 }}>
            <ActionBtn onClick={onAccept} disabled={loading} color="#16a34a" icon={CheckCircle2} tooltip="Accept" />
            <ActionBtn onClick={onCustomize} disabled={loading} color="#7c3aed" icon={Edit3} tooltip="Customize" />
            <ActionBtn onClick={onReject} disabled={loading} color="#dc2626" icon={XCircle} tooltip="Reject" />
            <ActionBtn onClick={onDefer} disabled={loading} color={STONE[500]} icon={Clock} tooltip="Defer" />
          </div>
        )}

        <button onClick={() => setExpanded(!expanded)} style={{
          border: 'none', background: 'none', cursor: 'pointer', color: STONE[400],
          transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s',
        }}>
          <ChevronDown size={16} />
        </button>
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 14px', borderTop: `1px solid ${STONE[100]}`, paddingTop: 14 }}>
          {c.sourcePassage && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, background: STONE[50],
              borderLeft: `3px solid ${BRAND.primary}40`, marginBottom: 12,
              fontSize: 12, fontStyle: 'italic', color: STONE[600], lineHeight: 1.5,
            }}>
              {c.sourcePassage}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, fontSize: 11 }}>
            <ScoreMeter label="Language Clarity" value={c.languageClarity} />
            <ScoreMeter label="Pattern Match" value={c.patternMatchQuality} />
            <ScoreMeter label="Param Extraction" value={c.paramExtractability} />
            <ScoreMeter label="Context Consistency" value={c.contextConsistency} />
          </div>
          {c.rejectionReason && (
            <div style={{
              marginTop: 12, padding: '8px 12px', borderRadius: 8, background: '#fef2f2',
              fontSize: 12, color: '#991b1b',
            }}>
              Rejection reason: {c.rejectionReason}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ onClick, disabled, color, icon: Icon, tooltip }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} disabled={disabled}
      title={tooltip}
      style={{
        width: 28, height: 28, borderRadius: 6, border: `1px solid ${color}30`,
        background: `${color}08`, color, cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: disabled ? 0.5 : 1,
      }}>
      <Icon size={14} />
    </button>
  );
}

function ScoreMeter({ label, value }) {
  const color = value >= 75 ? '#16a34a' : value >= 50 ? '#ca8a04' : BRAND.primary;
  return (
    <div>
      <div style={{ fontSize: 10, color: STONE[500], marginBottom: 4 }}>{label}</div>
      <div style={{ height: 6, borderRadius: 3, background: STONE[100], overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(value || 0, 100)}%`, height: '100%', borderRadius: 3, background: color, transition: 'width 0.3s' }} />
      </div>
      <div style={{ fontSize: 10, fontWeight: 600, color, marginTop: 2 }}>{value || 0}%</div>
    </div>
  );
}

function RejectModal({ candidate, onReject, onClose, loading }) {
  const [reason, setReason] = useState('');
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, padding: 24, width: 440,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: STONE[900], margin: '0 0 8px' }}>
          Reject Candidate
        </h3>
        <p style={{ fontSize: 13, color: STONE[500], margin: '0 0 16px' }}>
          {candidate.proposedRuleName}
        </p>
        <textarea value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Reason for rejection (optional)..."
          style={{
            width: '100%', minHeight: 80, padding: '10px 12px', borderRadius: 8,
            border: `1px solid ${STONE[200]}`, fontSize: 13, resize: 'vertical',
            boxSizing: 'border-box', fontFamily: 'Inter, sans-serif',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{
            padding: '8px 14px', fontSize: 13, borderRadius: 8,
            border: `1px solid ${STONE[200]}`, background: '#fff', color: STONE[600], cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={() => onReject(reason)} disabled={loading} style={{
            padding: '8px 14px', fontSize: 13, fontWeight: 600, borderRadius: 8,
            border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer',
          }}>Reject</button>
        </div>
      </div>
    </div>
  );
}

function CustomizeModal({ candidate, onSave, onClose, loading }) {
  const [name, setName] = useState(candidate.proposedRuleName);
  const [severity, setSeverity] = useState(candidate.proposedSeverity);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, padding: 24, width: 480,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: STONE[900], margin: '0 0 16px' }}>
          Customize Enforcement
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: STONE[700], display: 'block', marginBottom: 4 }}>
              Rule Name
            </label>
            <input value={name} onChange={e => setName(e.target.value)} style={{
              width: '100%', padding: '8px 12px', borderRadius: 8,
              border: `1px solid ${STONE[200]}`, fontSize: 13, boxSizing: 'border-box',
            }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: STONE[700], display: 'block', marginBottom: 4 }}>
              Severity
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['BLOCK', 'WARN', 'AUDIT'].map(s => (
                <button key={s} onClick={() => setSeverity(s)} style={{
                  flex: 1, padding: '8px', fontSize: 12, fontWeight: severity === s ? 600 : 400,
                  borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${severity === s ? BRAND.primary : STONE[200]}`,
                  background: severity === s ? BRAND.light : '#fff',
                  color: severity === s ? BRAND.primary : STONE[600],
                }}>{s}</button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button onClick={onClose} style={{
            padding: '8px 14px', fontSize: 13, borderRadius: 8,
            border: `1px solid ${STONE[200]}`, background: '#fff', color: STONE[600], cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={() => onSave({ customName: name, customSeverity: severity })} disabled={loading} style={{
            padding: '8px 14px', fontSize: 13, fontWeight: 600, borderRadius: 8,
            border: 'none', background: BRAND.primary, color: '#fff', cursor: 'pointer',
          }}>Accept with Changes</button>
        </div>
      </div>
    </div>
  );
}
